/**
 * AdvisorSession status keys — mirrors startups3
 * `src/sco/reference/advisor-session-statuses.js`.
 *
 * Keep this file as the single front-end copy: session status strings were
 * previously re-typed as literals in three separate sections, which drifts
 * silently when the backend state machine changes.
 */
export const SESSION_STATUS = {
  pending: 'pending',
  verifyWithExpert: 'verify_schedule_with_expert',
  verifyWithSeeker: 'verify_schedule_with_seeker',
  scheduleConfirmed: 'schedule_confirmed',
  live: 'live',
  completed: 'completed',
  canceled: 'canceled',
  expired: 'expired',
  timedOut: 'timed_out',
} as const

export type SessionStatusKey = (typeof SESSION_STATUS)[keyof typeof SESSION_STATUS]

/** Mirrors `ADVISOR_SESSION_TERMINAL_STATUSES`. */
const TERMINAL_STATUSES = new Set<string>([
  SESSION_STATUS.completed,
  SESSION_STATUS.canceled,
  SESSION_STATUS.expired,
  SESSION_STATUS.timedOut,
])

/** Statuses where a counterparty may accept one of the proposed times. */
const NEGOTIATING_STATUSES = new Set<string>([
  SESSION_STATUS.verifyWithExpert,
  SESSION_STATUS.verifyWithSeeker,
  SESSION_STATUS.pending,
])

const JOINABLE_STATUSES = new Set<string>([SESSION_STATUS.scheduleConfirmed, SESSION_STATUS.live])

/** Unknown / missing status is treated as terminal so we never offer live actions on it. */
export const isTerminalSession = (statusKey?: string | null) =>
  statusKey ? TERMINAL_STATUSES.has(statusKey) : true

export const isNegotiatingSession = (statusKey?: string | null) =>
  !!statusKey && NEGOTIATING_STATUSES.has(statusKey)

/**
 * Party who must accept (or counter) the current suggested times.
 * Mirrors startups3 `waitingPartyForProposedTimes` — legacy `pending` is
 * not emailed, so reminders stay off that status too.
 */
export const waitingPartyForProposedTimes = (statusKey?: string | null): 'expert' | 'seeker' | null => {
  if (statusKey === SESSION_STATUS.verifyWithExpert) {
    return 'expert'
  }
  if (statusKey === SESSION_STATUS.verifyWithSeeker) {
    return 'seeker'
  }
  return null
}

export const isJoinableSession = (statusKey?: string | null) =>
  !!statusKey && JOINABLE_STATUSES.has(statusKey)

/** Confirmed on the calendar and not yet live — pre-join actions live here. */
export const isScheduledSession = (statusKey?: string | null) =>
  statusKey === SESSION_STATUS.scheduleConfirmed

/** Recap copy when the room URL is opened after the call is over. */
export const roomEndedCopy = (statusKey?: string | null): string => {
  switch (statusKey) {
    case SESSION_STATUS.canceled:
      return 'This session was canceled.'
    case SESSION_STATUS.expired:
      return 'This session expired before it started.'
    case SESSION_STATUS.timedOut:
      return 'This session timed out.'
    case SESSION_STATUS.completed:
      return 'This session is complete. Review the recap on the session page.'
    default:
      return 'This call ended.'
  }
}

export const isNotJoinableError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : typeof error === 'string' ? error : ''
  return /ADVISOR_SESSION_NOT_JOINABLE|cannot be joined/i.test(message)
}

const sessionErrorText = (error: unknown): string => {
  if (typeof error === 'string') {
    return error
  }
  if (error instanceof Error && error.message) {
    return error.message
  }
  if (error && typeof error === 'object') {
    const record = error as {
      graphQLErrors?: Array<{ message?: string }>
      errors?: Array<{ message?: string }>
      message?: string
    }
    return record.graphQLErrors?.[0]?.message || record.errors?.[0]?.message || record.message || ''
  }
  return ''
}

const SESSION_ERROR_COPY: Record<string, string> = {
  ADVISOR_SESSION_ALREADY_ACTIVE:
    'You already have an open session with this advisor — a request waiting on a time, a confirmed booking, or a live call. Finish or cancel it in Sessions before booking another.',
  ADVISOR_SESSION_EXPERT_UNAVAILABLE: 'This advisor is not accepting paid sessions right now.',
  ADVISOR_SESSION_INPUT_INVALID: 'Check the session length, topic, and suggested times, then try again.',
  ADVISOR_SESSION_PRECHARGE_FAILED:
    'We could not authorize payment for this session. Check your card and try again.',
  ADVISOR_SESSION_PRECHARGE_REQUIRED: 'Payment must be authorized before this session can continue.',
  ADVISOR_SESSION_NOT_JOINABLE: 'This session cannot be joined until the schedule is confirmed.',
  ADVISOR_SESSION_INVALID_TRANSITION: 'That action is not available for this session right now.',
  ADVISOR_SESSION_REMINDER_FAILED: 'We could not send the reminder email. Try again.',
}

/** True when the seeker already has a non-terminal session with this expert. */
export const isAlreadyActiveSessionError = (error: unknown): boolean =>
  /ADVISOR_SESSION_ALREADY_ACTIVE/.test(sessionErrorText(error))

/** Maps session GraphQL error keys to founder-facing copy. */
export const sessionErrorMessage = (error: unknown, fallback: string): string => {
  const raw = sessionErrorText(error)
    .replace(/^ApolloError:\s*/i, '')
    .replace(/^CombinedGraphQLErrors:\s*/i, '')
    .replace(/^GraphQL error:\s*/i, '')
    .replace(/^Error:\s*/i, '')
    .trim()
  if (SESSION_ERROR_COPY[raw]) {
    return SESSION_ERROR_COPY[raw]
  }
  const mapped = Object.entries(SESSION_ERROR_COPY).find(([key]) => raw.includes(key))
  return mapped?.[1] || raw || fallback
}

/**
 * Whether the current user is the party who may accept a proposed slot.
 * Founder-proposed times wait on the expert; expert counters wait on the founder.
 * Legacy `pending` rows are treated as waiting on the expert (same as post-book default).
 */
export const isWaitingOnCurrentUser = (statusKey?: string | null, role?: string | null) =>
  (statusKey === SESSION_STATUS.verifyWithExpert && role === 'EXPERT') ||
  (statusKey === SESSION_STATUS.verifyWithSeeker && role === 'SEEKER') ||
  (statusKey === SESSION_STATUS.pending && role === 'EXPERT')

export type SessionListAction = 'confirm' | 'join' | 'rate' | 'open'

/**
 * Primary list CTA. Confirm / join beat rating so a live session never
 * hides Join behind a leftover prompt.
 */
export const sessionListAction = (
  statusKey?: string | null,
  role?: string | null,
  hasRated?: boolean
): SessionListAction => {
  if (isWaitingOnCurrentUser(statusKey, role)) {
    return 'confirm'
  }
  if (isJoinableSession(statusKey)) {
    return 'join'
  }
  if (statusKey === SESSION_STATUS.completed && !hasRated) {
    return 'rate'
  }
  return 'open'
}

export const SESSION_LIST_ACTION_LABEL: Record<SessionListAction, string> = {
  confirm: 'Confirm time',
  join: 'Join',
  rate: 'Rate',
  open: 'Open',
}

/** Badge palette for a session status. Shared so list and detail never disagree. */
export const sessionStatusPalette = (statusKey?: string | null): string => {
  switch (statusKey) {
    case SESSION_STATUS.scheduleConfirmed:
    case SESSION_STATUS.live:
      return 'green'
    case SESSION_STATUS.completed:
      return 'blue'
    case SESSION_STATUS.canceled:
    case SESSION_STATUS.expired:
    case SESSION_STATUS.timedOut:
      return 'red'
    case SESSION_STATUS.verifyWithExpert:
    case SESSION_STATUS.verifyWithSeeker:
    case SESSION_STATUS.pending:
      return 'yellow'
    default:
      return 'gray'
  }
}
